/**
 * Which identity artwork an Admin destination gets, and which ramp position.
 *
 * SILHOUETTE FIRST. Colour is a position on the Cluvik ramp and does no
 * identifying work: desaturate the whole family and Tenants, Product, Commerce
 * and Governance must still be tellable apart by shape alone. That is why two
 * identities share a ramp position rather than a ninth colour being invented —
 * the approved system does the same (violet carries both CRM Home and CRM
 * Console) and leaves the distinguishing to geometry.
 *
 * ARCHETYPE IS NOT THE SAME AS DESTINATION. Several destinations legitimately
 * draw the same form: Overview and Health are both "state of the estate", and
 * giving them different artwork would claim a distinction the product does not
 * make. The map below is destination -> {archetype, ramp}, so a new screen in
 * an existing area reuses the area's mark instead of commissioning one.
 *
 * A destination with no entry resolves to a stable hash rather than to a
 * default, because a shared default silently merges identities — every new area
 * would look like every other new area and nobody would notice until there were
 * four of them.
 */
import { RAMP_KEYS } from '../tokens/identityTokens';

/** Archetypes this build can draw. Adding one means adding geometry. */
export const ARCHETYPES = [
  'overview',    // stacked plates — the estate at a glance
  'tenant',      // a building block with a lit face
  'product',     // a boxed module
  'commerce',    // a price tag / receipt form
  'connection',  // two linked bodies with a joint
  'platform',    // a launch/release wedge
  'governance',  // a shield
  'operator',    // a figure with a key notch
];

const MAP = {
  '/': { archetype: 'overview', ramp: 'violet' },
  '/tenants': { archetype: 'tenant', ramp: 'indigo' },
  '/tenants/create': { archetype: 'tenant', ramp: 'iris' },
  '/users': { archetype: 'operator', ramp: 'indigo' },
  '/modules': { archetype: 'product', ramp: 'blue' },
  '/license-catalog': { archetype: 'product', ramp: 'blue' },
  '/quotas': { archetype: 'product', ramp: 'blue' },
  '/subscriptions': { archetype: 'commerce', ramp: 'azure' },
  '/commercial': { archetype: 'commerce', ramp: 'azure' },
  '/connections/profiles': { archetype: 'connection', ramp: 'sky' },
  '/integrations': { archetype: 'connection', ramp: 'sky' },
  '/releases': { archetype: 'platform', ramp: 'cyan' },
  '/platform-drift': { archetype: 'platform', ramp: 'cyan' },
  '/audit-logs': { archetype: 'governance', ramp: 'aqua' },
  '/operators': { archetype: 'governance', ramp: 'aqua' },
};

/** Stable across renders and across builds — a hash, not a counter. */
function hashPosition(key) {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) {
    h = (h * 31 + key.charCodeAt(i)) >>> 0;
  }
  return RAMP_KEYS[h % RAMP_KEYS.length];
}

/**
 * `{archetype, ramp}` for a destination path. Unknown paths get the overview
 * form at a hashed ramp position: recognisably un-designed, stable, and never
 * silently identical to a designed one.
 */
export function identityForPath(path) {
  const key = String(path || '');
  if (MAP[key]) return MAP[key];
  return { archetype: 'overview', ramp: hashPosition(key) };
}

export const IDENTITY_MAP = MAP;
